// app/components/product-modal/product-variants.tsx
// Component to display product variants from budflow_flavors grouping
//
// Related files:
// - ~/utils/cache-manager.ts - Unified caching system
// - ~/components/product-modal/modal-product-card.tsx - Parent component
// - ~/components/global-product-modal.tsx - Modal context

import { useState, useEffect } from 'react';
import { useStore } from '~/context/store-context';
import { createCache, createCacheKey, CACHE_PRESETS } from '~/utils/cache-manager';

interface VariantProduct {
  id: string;
  name: string;
  image: string;
  brand: string;
  category: string;
  price: number;
  salePrice?: number;
  size: string;
  treez_id: string;
}

// ⚡ PERFORMANCE: Unified cache for product variants
// Uses shared CacheManager for consistent caching behavior across the app
// TTL: 10 minutes (variants don't change frequently)
// Max size: 30 entries (flavor group+store combinations)
// Cache key: group_id-store (all products in same group share cache)
const variantsCache = createCache<VariantProduct[]>(
  {
    ttl: 10 * 60 * 1000, // 10 minutes
    maxSize: 30, // Fewer entries than other caches (less common use case)
  },
  'ProductVariantsCache',
);

interface ProductVariantsProps {
  currentProductId: string;
  currentProductName: string;
  budflowFlavors?: {
    group_id: string;
    group_name: string;
    products: string[];
  };
  onVariantChange: (variantId: string) => void;
}

export default function ProductVariants({
  currentProductId,
  currentProductName,
  budflowFlavors,
  onVariantChange,
}: ProductVariantsProps) {
  // Initialize with current product to prevent layout shift
  const currentProduct: VariantProduct = {
    id: currentProductId,
    name: currentProductName,
    image: '', // We don't need image for dropdown
    brand: '',
    category: '',
    price: 0,
    size: '',
    treez_id: '',
  };

  const [variants, setVariants] = useState<VariantProduct[]>([currentProduct]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedVariantId, setSelectedVariantId] = useState(currentProductId);
  const [hasLoadedVariants, setHasLoadedVariants] = useState(false); // ⚡ Track if variants have been loaded
  const { selectedStore } = useStore();

  // Function to find the longest common prefix
  const findCommonPrefix = (names: string[]): string => {
    if (names.length === 0) return '';
    if (names.length === 1) return '';

    // Sort names to compare first and last (they'll be most different)
    const sorted = [...names].sort();
    const first = sorted[0];
    const last = sorted[sorted.length - 1];

    let i = 0;
    while (i < first.length && first[i] === last[i]) {
      i++;
    }

    // Find the last space before the divergence to keep whole words
    const commonPrefix = first.substring(0, i);
    const lastSpaceIndex = commonPrefix.lastIndexOf(' ');

    return lastSpaceIndex > 0 ? commonPrefix.substring(0, lastSpaceIndex + 1) : commonPrefix;
  };

  // Function to extract unique part of product name
  const getUniqueName = (fullName: string, commonPrefix: string): string => {
    if (!commonPrefix) return fullName;
    const unique = fullName.substring(commonPrefix.length).trim();
    return unique || fullName;
  };

  // ⚡ PERFORMANCE: Lazy-load variants only when user interacts with dropdown
  // Uses group-level caching - all products in same flavor group share cached variants
  const fetchVariants = async () => {
    if (!budflowFlavors || !budflowFlavors.products.length || hasLoadedVariants) return;

    // budflowFlavors.products contains treez_id values
    const variantTreezIds = budflowFlavors.products;

    if (variantTreezIds.length <= 1) return; // Only current product, no need to fetch

    // ⚡ CACHE KEY: Use group_id + store (not product-specific)
    // All products in the same flavor group share the same variants
    const cacheKey = createCacheKey(budflowFlavors.group_id, selectedStore);

    // Check cache first
    const cached = variantsCache.get(cacheKey);
    if (cached !== null && cached.length > 0) {
      // Cache hit - use immediately, no API call needed
      setVariants(cached);
      setHasLoadedVariants(true);
      setIsLoading(false);
      return;
    }

    // ⚡ RACE CONDITION PREVENTION: Check if already fetching
    if (variantsCache.isFetching(cacheKey)) {
      return; // Another fetch is already in progress, skip duplicate
    }

    setIsLoading(true);
    setHasLoadedVariants(true); // Mark as loaded to prevent duplicate fetches

    // Mark as fetching to prevent concurrent requests
    variantsCache.markFetching(cacheKey);

    try {
      // Fetch all variant products by treez_id in parallel (including current product)
      // Products not in stock will be filtered out automatically
      const variantPromises = variantTreezIds.map(async (treezId) => {
        try {
          const response = await fetch(
            `/api/product_by_treez_id?treez_id=${treezId}&store=${selectedStore}`,
          );
          if (!response.ok) return null;
          const data = await response.json();

          // Only return products that are found and in stock
          if (!data.found || !data.inStock) return null;

          return data.product;
        } catch (error) {
          console.error(`Error fetching variant ${treezId}:`, error);
          return null;
        }
      });

      const results = await Promise.all(variantPromises);
      const validVariants = results.filter((v): v is VariantProduct => v !== null);

      // Update variants with fetched data
      if (validVariants.length > 0) {
        setVariants(validVariants);

        // ⚡ CACHE THE FULL VARIANT LIST (shared across all products in group)
        variantsCache.set(cacheKey, validVariants);
      } else {
        // Cache empty array to prevent refetch spam (automatically clears fetching flag)
        variantsCache.set(cacheKey, []);
      }
    } catch (error) {
      console.error('Error fetching variants:', error);

      // Cache empty array to prevent refetch spam (automatically clears fetching flag)
      variantsCache.set(cacheKey, []);
    } finally {
      setIsLoading(false);
    }
  };

  // ⚡ PERFORMANCE: Preload variants on hover for better UX
  // This ensures variants are ready when user opens the dropdown
  const handleMouseEnter = () => {
    if (!hasLoadedVariants && budflowFlavors && budflowFlavors.products.length > 1) {
      fetchVariants();
    }
  };

  // Handle dropdown focus/click to trigger lazy loading (if not already loaded)
  const handleDropdownInteraction = () => {
    if (!hasLoadedVariants && budflowFlavors && budflowFlavors.products.length > 1) {
      fetchVariants();
    }
  };

  // Update selected variant when current product changes
  useEffect(() => {
    setSelectedVariantId(currentProductId);
  }, [currentProductId]);

  // ⚡ LAYOUT STABILITY: Always show placeholder if product has variants (from cached data)
  // This ensures the dropdown structure is part of the modal from the start
  const hasVariants = budflowFlavors && budflowFlavors.products.length > 1;

  // Don't render if product doesn't have variants
  if (!hasVariants) {
    return null;
  }

  const handleVariantChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newVariantId = e.target.value;
    setSelectedVariantId(newVariantId);
    onVariantChange(newVariantId);
  };

  // Get all variant names including current product
  const allNames = variants.map((v) => v.name.toUpperCase());

  // ⚡ LAYOUT STABILITY: Always calculate common prefix to prevent layout shift
  // If we only have the current product but budflow_flavors has multiple products,
  // use the group_name to predict the common prefix
  let commonPrefix = '';
  if (variants.length === 1 && budflowFlavors && budflowFlavors.products.length > 1) {
    // Use the group name as the common prefix if available
    if (budflowFlavors.group_name) {
      commonPrefix = budflowFlavors.group_name.toUpperCase() + ' ';
    } else {
      // Fallback: try to extract prefix from current product name
      const currentName = currentProductName.toUpperCase();
      const words = currentName.split(' ');
      if (words.length > 2) {
        // Take everything except the last word as potential prefix
        commonPrefix = words.slice(0, -1).join(' ') + ' ';
      }
    }
  } else {
    commonPrefix = findCommonPrefix(allNames);
  }

  // ⚡ LAYOUT STABILITY: Always calculate display text for current product to prevent shift
  const getCurrentProductDisplayText = () => {
    const uniqueName = getUniqueName(currentProductName.toUpperCase(), commonPrefix);
    return uniqueName;
  };

  return (
    <div
      className="mx-auto mb-6 w-full"
      aria-labelledby="variant-selector-label"
      onMouseEnter={handleMouseEnter}
    >
      <div className="relative w-full">
        {/* Offset Shadow */}
        <div className="absolute inset-0 translate-x-0.5 translate-y-0.5 bg-black" />
        <div className="relative w-full">
          <div className="relative">
            <select
              id="variant-selector-label"
              value={selectedVariantId}
              onChange={handleVariantChange}
              onFocus={handleDropdownInteraction}
              onClick={handleDropdownInteraction}
              onKeyDown={(e) => {
                // Native <select> doesn't open on Enter; use the picker API so keyboard
                // users can open the variant list (Space/Arrow keys work natively too).
                if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
                  const el = e.currentTarget as HTMLSelectElement & { showPicker?: () => void };
                  if (typeof el.showPicker === 'function') {
                    e.preventDefault();
                    try {
                      el.showPicker();
                    } catch {
                      // showPicker can throw without a user gesture — ignore.
                    }
                  }
                }
              }}
              className="font-poppins min-h-[44px] w-full appearance-none bg-gray-100 px-4 py-2 pr-10 text-sm text-black ring ring-gray-200 focus:ring-2 focus:ring-[#ffd100] focus:ring-offset-2 focus:outline-none"
              aria-label="Select product variant"
              aria-busy={isLoading && variants.length === 1}
              style={{ minHeight: '3.5rem' }} // ⚡ LAYOUT STABILITY: Fixed height to prevent shift
            >
              {/* ⚡ LAYOUT STABILITY: Always show current product first with consistent formatting */}
              <option key={currentProductId} value={currentProductId}>
                {getCurrentProductDisplayText()}
              </option>
              {/* Show loading text inside dropdown while fetching variants */}
              {isLoading && variants.length === 1 && (
                <option value="" disabled>
                  Loading Variations...
                </option>
              )}
              {/* Show other variants once loaded (lazy-loaded on interaction) */}
              {variants
                .filter((variant) => variant.id !== currentProductId)
                .map((variant) => {
                  const uniqueName = getUniqueName(variant.name.toUpperCase(), commonPrefix);
                  const displayPrice = variant.salePrice || variant.price;
                  const currentVariant = variants.find((v) => v.id === currentProductId);
                  const currentPrice = currentVariant
                    ? currentVariant.salePrice || currentVariant.price
                    : 0;

                  // Show full price if different from current variant
                  let priceDisplay = '';
                  if (
                    variant.id !== currentProductId &&
                    Math.abs(displayPrice - currentPrice) > 0.01
                  ) {
                    priceDisplay = ` - $${displayPrice.toFixed(2)}`;
                  }

                  return (
                    <option key={variant.id} value={variant.id}>
                      {uniqueName}
                      {priceDisplay}
                    </option>
                  );
                })}
            </select>

            {/* Dropdown Arrow */}
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <svg
                className="h-5 w-5"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  d="M5 7.5L10 12.5L15 7.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
